import { type JSX, type ReactNode, useId } from 'react';

import { darken, type Theme, useTheme } from '@mui/material/styles';

import { isPrimitive } from '@craft/lib/primitives';

import { type MuiThemeCallbackOrValue } from '../../../types/mui/sx';
import { type QuantitativeSize } from '../../../types/sizes';
import { mergeSx } from '../../../util/mui/sx';
import { parseQuantitativeSize, quantitativeSizeToString } from '../../../util/sizes';
import { Actions } from '../../actions/Actions';
import { type ActionsProp } from '../../actions/types';
import { Icon } from '../../icons/Icon';
import { IconDiscreteSize, type IconProp, type IconProps, type IconSize } from '../../icons/types';
import { Stack } from '../../structural/Stack';
import { type LineClamp } from '../../typography/types';
import { ConditionallyRender } from '../../util/ConditionallyRender';
import { IfNodeRenders } from '../../util/IfNodeRenders';
import { GenericMenuItemCheckbox } from '../checkboxes/GenericMenuItemCheckbox';
import {
  type ListItemComponent,
  type ListItemDefaultComponent,
  ListItemIndicatorMode,
} from '../types';

import { ListItemDescription, type ListItemDescriptionProps } from './ListItemDescription';
import { ListItemTitle, type ListItemTitleProps } from './ListItemTitle';
import {
  MenuItemContainer,
  type MenuItemContainerPartialProps,
  type MenuItemContainerProps,
} from './MenuItemContainer';

interface CheckboxRenderProps {
  readonly inputProps: {
    readonly 'aria-labelledby': string;
  };
}

interface ParseMenuItemLeftPaddingParams {
  readonly isSelectable?: boolean;
  readonly nestedIndentation?: QuantitativeSize<'px' | 'rem'>;
  readonly nestedLevel?: number;
}

const parseMenuItemLeftPadding = ({
  isSelectable = false,
  nestedIndentation = '0.25rem',
  nestedLevel = 0,
}: ParseMenuItemLeftPaddingParams) => {
  const basePadding = isSelectable ? '0.5rem' : '1rem';
  if (nestedIndentation) {
    const [parsed, unit] = parseQuantitativeSize(nestedIndentation);
    return `calc(${quantitativeSizeToString(parsed * nestedLevel, unit ?? 'px')} + ${basePadding})`;
  }
  return basePadding;
};

export type GenericMenuItemProps<C extends ListItemComponent = ListItemDefaultComponent> =
  MenuItemContainerProps<
    C,
    {
      readonly actions?: ActionsProp;
      readonly checkbox?: (props: CheckboxRenderProps) => ReactNode;
      readonly component?: C;
      readonly description?: ReactNode;
      /**
       * Defines the line clamp, {@link LineClamp}, for the description of the
       * {@link GenericMenuItem}.  This {@link LineClamp} will be applied to the 'description'
       * prop if it is defined and is a primitive value, {@link Primitive}.
       */
      readonly descriptionLineClamp?: LineClamp;
      readonly descriptionProps?: Omit<ListItemDescriptionProps, 'children' | 'lineClamp'>;
      readonly emphasisColor?: MuiThemeCallbackOrValue<string>;
      readonly gap?: ((t: Theme) => string) | QuantitativeSize;
      readonly highlightedColor?: MuiThemeCallbackOrValue<string>;
      readonly icon?: IconProp;
      readonly iconProps?: Omit<IconProps, 'dimension' | 'size'>;
      readonly iconSize?: IconSize;
      readonly isLoading?: boolean;
      /**
       * Defines whether or not the {@link GenericMenuItemProps} should be considered to be in a
       * "locked" state.
       *
       * In a "locked" state, the {@link GenericMenuItemProps} will function as if it is disabled,
       * but will not appear to be disabled.  This is useful for preventing multiple clicks of the
       * {@link GenericMenuItemProps} in quick succession.
       */
      readonly isLocked?: boolean;
      readonly isSelectable?: boolean;
      readonly isSelected?: boolean;
      readonly isSelectionDisabled?: boolean;
      /**
       * Defines the line clamp, {@link LineClamp}, for the primary text content of the
       * {@link GenericMenuItem}.  This {@link LineClamp} will be applied to the 'children' of the
       * {@link GenericMenuItem} if the 'children' prop is defined as a primitive value,
       * {@link Primitive}, or the 'title' prop if it is defined and is a primitive value,
       * {@link Primitive}.
       */
      readonly lineClamp?: LineClamp;
      readonly nestedIndentation?: QuantitativeSize<'px' | 'rem'>;
      readonly nestedLevel?: number;
      readonly selectedIconProps?: Omit<IconProps, 'dimension' | 'size'>;
      readonly selectionModes?: ListItemIndicatorMode[];
      readonly title?: ReactNode;
      readonly titleProps?: Omit<ListItemTitleProps, 'children' | 'lineClamp'>;
      readonly whiteSpace?: 'nowrap' | 'pre-line';
    } & MenuItemContainerPartialProps<C>
  >;

export const GenericMenuItem = <C extends ListItemComponent = ListItemDefaultComponent>({
  actions,
  checkbox,
  description,
  descriptionProps,
  emphasisColor = t => t.palette.primary.main,
  gap = '16px',
  highlightedColor = t => t.palette.action.selected,
  icon,
  iconProps,
  iconSize = IconDiscreteSize.Medium,
  isLoading,
  isLocked = false,
  isSelectable: propIsSelectable,
  isSelected,
  isSelectionDisabled = false,
  lineClamp = 1,
  nestedIndentation,
  nestedLevel = 0,
  selectedIconProps,
  selectionModes = [ListItemIndicatorMode.Checkbox],
  title,
  titleProps,
  whiteSpace = 'nowrap',
  ...props
}: GenericMenuItemProps<C>): JSX.Element => {
  const contentId = useId();
  const theme = useTheme();

  const selectedBackgroundColor = (t: Theme) =>
    typeof highlightedColor === 'function' ? highlightedColor(t) : highlightedColor;

  const selectedTextColor = (t: Theme) =>
    typeof emphasisColor === 'function' ? emphasisColor(t) : emphasisColor;

  const isSelectable = propIsSelectable ?? isSelected !== undefined;

  return (
    <MenuItemContainer<C>
      aria-checked={isSelectable ? isSelected : undefined}
      role={isSelectable ? 'menuitemcheckbox' : 'menuitem'}
      {...(props as MenuItemContainerProps<C>)}
      disableRipple
      disableTouchRipple
      sx={mergeSx(
        t => ({
          '&:focus,&:focus-visible,&:focus-within': {
            backgroundColor: isSelectionDisabled
              ? undefined
              : isSelected && selectionModes.includes(ListItemIndicatorMode.Background)
                ? darken(selectedBackgroundColor(t), 0.1)
                : t.palette.action.focus,
          },
          '&:hover:not(:focus,:focus-visible,:focus-within)': {
            backgroundColor: isSelectionDisabled
              ? undefined
              : isSelected && selectionModes.includes(ListItemIndicatorMode.Background)
                ? selectedBackgroundColor(t)
                : t.palette.action.hover,
          },
          alignItems: 'center',
          backgroundColor: isSelectionDisabled
            ? undefined
            : isSelected && selectionModes.includes(ListItemIndicatorMode.Background)
              ? selectedBackgroundColor(t)
              : undefined,
          display: 'flex',
          flexDirection: 'row',
          gap: typeof gap === 'function' ? gap(t) : quantitativeSizeToString(gap, 'px'),
          justifyContent: 'space-between',
          minHeight: 'auto',
          paddingLeft: parseMenuItemLeftPadding({
            isSelectable,
            nestedIndentation,
            nestedLevel,
          }),
          paddingRight: t.spacing(2),
          paddingY: t.spacing(1),
          whiteSpace,
        }),
        props.sx,
        isLocked || isLoading || isSelectionDisabled ? { pointerEvents: 'none' } : {},
      )}
    >
      <Stack
        alignItems='center'
        direction='row'
        gap={typeof gap === 'function' ? gap : quantitativeSizeToString(gap, 'px')}
        maxWidth='100%'
        overflow='hidden'
        width='100%'
      >
        <ConditionallyRender
          show={selectionModes.includes(ListItemIndicatorMode.Checkbox) && isSelectable}
        >
          {checkbox?.({
            inputProps: {
              'aria-labelledby': contentId,
            },
          }) ?? (
            <GenericMenuItemCheckbox
              inputProps={{
                'aria-labelledby': contentId,
              }}
              isDisabled={isSelectionDisabled}
              isSelected={isSelected}
            />
          )}
        </ConditionallyRender>
        <Stack
          alignItems='center'
          direction='row'
          flexGrow={100}
          gap={typeof gap === 'function' ? gap : quantitativeSizeToString(gap, 'px')}
          id={contentId}
          /* Apply a slight adjustment to the left to account for the padding around the checkbox.
             This helps keep the space between the checkbox, the icon and the content of the menu
             item evenly spaced. */
          left={
            selectionModes.includes(ListItemIndicatorMode.Checkbox) && isSelectable ? '-4px' : 0
          }
          minWidth={0}
          overflow='hidden'
          position='relative'
        >
          <Icon
            {...iconProps}
            {...(isSelected ? selectedIconProps : undefined)}
            icon={icon}
            isLoading={isLoading}
            size={iconSize}
            style={{
              color:
                !isSelectionDisabled &&
                isSelectable &&
                isSelected &&
                selectionModes.includes(ListItemIndicatorMode.TextColor)
                  ? selectedTextColor(theme)
                  : undefined,
              ...iconProps?.style,
            }}
          />
          <Stack
            direction='column'
            flexGrow={100}
            maxWidth='100%'
            minWidth={0}
            sx={t => ({
              color:
                !isSelectionDisabled &&
                isSelected &&
                selectionModes.includes(ListItemIndicatorMode.TextColor)
                  ? selectedTextColor(t)
                  : undefined,
              fontWeight:
                isSelected && selectionModes.includes(ListItemIndicatorMode.FontWeight) ? 600 : 400,
              overflowX: 'hidden',
            })}
          >
            <IfNodeRenders node={title}>
              {isPrimitive(title, { exclude: ['null', 'undefined'] }) ? (
                <ListItemTitle {...titleProps} inherit={['fontWeight']} lineClamp={lineClamp}>
                  {title}
                </ListItemTitle>
              ) : (
                title
              )}
            </IfNodeRenders>
            <IfNodeRenders node={description}>
              {typeof description === 'string' || typeof description === 'number' ? (
                <ListItemDescription {...descriptionProps}>{description}</ListItemDescription>
              ) : (
                description
              )}
            </IfNodeRenders>
            <Stack alignItems='center' direction='row'>
              {isPrimitive(props.children, { exclude: ['null', 'undefined'] }) ? (
                <ListItemTitle {...titleProps} lineClamp={lineClamp}>
                  {props.children}
                </ListItemTitle>
              ) : (
                props.children
              )}
            </Stack>
          </Stack>
        </Stack>
      </Stack>
      <Actions>{actions}</Actions>
    </MenuItemContainer>
  );
};
